import { useAuthContext } from "@/providers/auth-provider";
import { isNonProduction } from "@/utils/common";
import { getCORSSafeURL } from "@/utils/url";
import {
  useCountryOptions,
  useDistributorBranding,
  useDistributorProfile,
} from "creso-core/hooks";
import type {
  DistributorBranding,
  DistributorProfile,
  MarketingResource,
} from "creso-core/types";
import {
  isEmployeeUser,
  parsePhoneForMarketingDisplay,
} from "creso-core/utils";
import html2canvas from "html2canvas-pro";
import { DownloadIcon, Loader2 } from "lucide-react";
import { sleep } from "radash";
import React, { useCallback, useMemo, useRef, useState } from "react";
import { Button, cn } from "web-design-system";

const MFD_DISCLAIMER_L1 =
  "Mutual Fund investments are subject to market risks.";
const MFD_DISCLAIMER_L2 = "Read all scheme related documents carefully.";
export const MFD_ROLE_LABEL = "Mutual Fund Distributor";

function getFlatFlagImageUrl(iso: string): string | null {
  const c = iso.trim().toUpperCase();
  if (c.length !== 2 || !/^[A-Z]{2}$/.test(c)) return null;
  return `https://flagcdn.com/w40/${c.toLowerCase()}.png`;
}

export function FlatPhoneFlag({
  url,
  className,
}: {
  url?: string | null;
  className?: string;
}) {
  if (!url) {
    return null;
  }
  return (
    <img src={url} alt="" aria-hidden className={cn("shrink-0", className)} />
  );
}

export function MarketingFooterDisclaimer({
  className,
}: {
  className?: string;
}) {
  return (
    <span
      className={cn(
        "leading-snug text-[12px] text-current opacity-60 text-right flex flex-col items-end",
        className
      )}
    >
      <span>{MFD_DISCLAIMER_L1}</span>
      <span>{MFD_DISCLAIMER_L2}</span>
    </span>
  );
}

export function useMarketingCreativeContext() {
  const { user, employee } = useAuthContext();
  const { data: profile } = useDistributorProfile();
  const { data: branding } = useDistributorBranding();
  const countryOptions = useCountryOptions();

  const contactNumber =
    isEmployeeUser(user.user_type) && employee?.phone_number
      ? employee?.phone_number
      : profile?.phone_number;

  const contactPhoneDisplay = useMemo(() => {
    const parsed = parsePhoneForMarketingDisplay(contactNumber ?? "", {
      defaultCountry: "IN",
    });
    const { iso, callingCodeLabel, nationalNumber } = parsed;
    if (countryOptions.length === 0) {
      return {
        flagImageUrl: getFlatFlagImageUrl(iso),
        callingCodeLabel,
        nationalNumber,
      };
    }
    const known = countryOptions.some(
      (option) => option.value.trim().toUpperCase() === iso
    );
    return {
      flagImageUrl: known ? getFlatFlagImageUrl(iso) : null,
      callingCodeLabel,
      nationalNumber,
    };
  }, [contactNumber, countryOptions]);

  const arn = `ARN-${profile?.arn_code}`;

  return {
    profile,
    branding,
    arn,
    contactFlagImageUrl: contactPhoneDisplay.flagImageUrl,
    contactCallingCodeLabel: contactPhoneDisplay.callingCodeLabel,
    contactNationalNumber: contactPhoneDisplay.nationalNumber,
  };
}

export type MarketingCreativeSharedProps = {
  src: string;
  profile?: DistributorProfile;
  arn?: string;
  contactFlagImageUrl?: string | null;
  contactCallingCodeLabel?: string;
  contactNationalNumber?: string;
  branding?: DistributorBranding;
  metadata?: Record<string, unknown>;
};

export function MarketingCreativePreview({
  src,
  profile,
  arn,
  contactFlagImageUrl,
  contactCallingCodeLabel,
  contactNationalNumber,
  branding,
  metadata,
  className,
}: MarketingCreativeSharedProps & { className?: string }) {
  return (
    <div
      className={cn(
        "rounded-2xl border border-border bg-white overflow-hidden relative",
        className
      )}
    >
      <img
        src={getCORSSafeURL(src)}
        alt={branding?.brand_name || profile?.name || "Marketing creative"}
        className="aspect-[3/4] object-center w-full bg-white"
      />
      <div
        className={cn(
          "flex flex-col gap-[8px] px-[20px] min-h-[86px] justify-center absolute bottom-0 left-0 w-full text-white w-full",
          metadata?.color === "dark" && "text-black",
          !branding?.tag_line ? "items-center" : "items-start"
        )}
      >
        <div className="flex items-center justify-between gap-[12px] w-full">
          <div className="flex min-w-0 items-center gap-[8px]">
            {branding?.brand_logo ? (
              <div className="overflow-hidden rounded-lg shrink-0">
                <img
                  src={getCORSSafeURL(branding.brand_logo as string)}
                  alt={branding?.brand_name}
                  className="w-[40px] h-[40px] object-cover"
                />
              </div>
            ) : null}
            <div className="min-w-0">
              <div className="line-clamp-1 text-[12px] font-semibold">
                {branding?.brand_name || profile?.name}
              </div>
              <div className="line-clamp-2 text-[10px] opacity-60">
                {branding?.tag_line}
              </div>
            </div>
          </div>
          <div className="max-w-1/2 text-right shrink-0">
            <div className="line-clamp-1 text-[12px] font-semibold">{arn}</div>
            <div className="line-clamp-2 text-[10px] opacity-60">
              {MFD_ROLE_LABEL}
            </div>
          </div>
        </div>
        <div className="flex items-end justify-between gap-[12px] w-full">
          <div
            className="flex min-w-0 items-center gap-[8px] text-[12px] font-semibold"
            aria-label={`${contactCallingCodeLabel} ${contactNationalNumber}`}
          >
            <span className="flex shrink-0 items-center gap-[4px] pl-[2px]">
              <FlatPhoneFlag
                url={contactFlagImageUrl}
                className="h-[10px] w-[12px] rounded-xs"
              />
              <span>{contactCallingCodeLabel}</span>
            </span>
            <span className="min-w-0">{contactNationalNumber}</span>
          </div>
          <MarketingFooterDisclaimer className="text-[7px]" />
        </div>
      </div>
    </div>
  );
}

export const CreativeDownloadImage = React.forwardRef<
  HTMLDivElement,
  MarketingCreativeSharedProps
>((props, ref) => {
  const {
    src,
    profile,
    arn,
    contactFlagImageUrl,
    contactCallingCodeLabel,
    contactNationalNumber,
    branding,
    metadata,
  } = props;

  return (
    <div
      ref={ref}
      style={{
        position: "fixed",
        top: "-9999px",
        left: "-9999px",
        height: "1200px",
        width: "900px",
        backgroundImage: `url(${getCORSSafeURL(src)})`,
        backgroundSize: "contain",
        backgroundRepeat: "no-repeat",
        backgroundPosition: "center",
      }}
      className="relative overflow-hidden"
    >
      <div
        className={cn(
          "flex flex-col px-[152px] min-h-[200px] justify-center text-white gap-[28px] font-sm absolute bottom-0 left-0 w-full",
          metadata?.color === "dark" && "text-black",
          !branding?.tag_line ? "items-center" : "items-start"
        )}
      >
        <div className="flex flex-row items-center gap-[8px] justify-between w-full">
          <div className="flex flex-row items-center gap-[16px]">
            {branding?.brand_logo ? (
              <div className="overflow-clip rounded-[16px] shrink-0">
                <img
                  src={getCORSSafeURL(branding.brand_logo as string)}
                  alt={branding?.brand_name}
                  className="h-[64px] w-[64px]"
                />
              </div>
            ) : null}
            <div className="flex flex-col gap-[4px]">
              <span className="text-[20px] leading-[1.2] font-semibold">
                {branding?.brand_name || profile?.name}
              </span>
              <div className="text-[16px] leading-[1.2] line-clamp-2 text-current opacity-60">
                {branding?.tag_line}
              </div>
            </div>
          </div>
          <div className="flex flex-col max-w-1/2 gap-[4px] items-end-safe text-right shrink-0">
            <span className="text-[20px] leading-[1.2] font-semibold">
              {arn}
            </span>
            <div className="text-[16px] leading-[1.2] break-all line-clamp-2 text-current opacity-60">
              {MFD_ROLE_LABEL}
            </div>
          </div>
        </div>
        <div className="flex flex-row items-center gap-[8px] justify-between w-full">
          <div
            className="flex min-w-0 items-center text-[20px] font-bold leading-tight gap-[16px]"
            aria-label={`${contactCallingCodeLabel} ${contactNationalNumber}`}
          >
            <span className="flex shrink-0 items-center gap-[1.5px] justify-center">
              <FlatPhoneFlag
                url={contactFlagImageUrl}
                className="h-[16px] w-[24px] rounded-xs ml-[2px]"
              />
              <span>{contactCallingCodeLabel}</span>
            </span>
            <span className="min-w-0">{contactNationalNumber}</span>
          </div>
          <MarketingFooterDisclaimer />
        </div>
      </div>
    </div>
  );
});

CreativeDownloadImage.displayName = "CreativeDownloadImage";

const saveAs = (uri: string, filename: string) => {
  const link = document.createElement("a");
  link.href = uri;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

type MarketingCreativePreviewPanelProps = {
  item: MarketingResource;
};

export function MarketingCreativePreviewPanel({
  item,
}: MarketingCreativePreviewPanelProps) {
  const {
    profile,
    branding,
    arn,
    contactFlagImageUrl,
    contactCallingCodeLabel,
    contactNationalNumber,
  } = useMarketingCreativeContext();
  const downloadRef = useRef<HTMLDivElement>(null);
  const [isDownloading, setIsDownloading] = useState(false);
  const previewSrc = item?.attachments[0]?.attachment ?? "";

  const handleDownload = useCallback(async () => {
    try {
      setIsDownloading(true);

      if (!downloadRef.current) {
        return;
      }

      const canvas = await html2canvas(downloadRef.current, {
        scrollY: -window.scrollY,
        useCORS: true,
        width: 900,
        height: 1200,
        scale: 1,
        allowTaint: true,
        logging: isNonProduction(),
        onclone() {
          return sleep(1000);
        },
      });

      saveAs(
        canvas.toDataURL("image/jpeg", 1.0),
        `${branding?.brand_name || profile?.name}.jpg`
      );
    } finally {
      setIsDownloading(false);
    }
  }, [branding, profile]);

  if (!previewSrc) {
    return null;
  }

  const sharedProps: MarketingCreativeSharedProps = {
    src: previewSrc,
    branding,
    profile,
    arn,
    metadata: item.metadata,
    contactFlagImageUrl,
    contactCallingCodeLabel,
    contactNationalNumber,
  };

  return (
    <>
      <MarketingCreativePreview {...sharedProps} className="mx-auto w-full" />
      <Button
        type="button"
        className="w-full"
        onClick={handleDownload}
        disabled={isDownloading}
      >
        {isDownloading ? (
          <Loader2 className="size-4 animate-spin" aria-hidden />
        ) : (
          <DownloadIcon className="size-4" />
        )}
        Download
      </Button>
      <CreativeDownloadImage ref={downloadRef} {...sharedProps} />
    </>
  );
}
